// Note: cribbing nearly all of this from DeviasKit 3.1 src/views/order/OrderDetailsView/index.js
// They enclose the view in a <Page /> component that does a lot of excellent stuff.
//   We should probably do much of the same, eventually

import React, { useCallback, useEffect, useState } from 'react';
import { DateTime, Duration } from 'luxon';

import { Container, Grid } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import axios from '../../utils/axios';
import useIsMountedRef from '../../hooks/useIsMountedRef';

import DA from '../../store/ReduxAccess';

import ControlPanel from './ControlPanel';
import ReportPreview from './ReportPreview';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

const replaceNulls = (refQuad) => {
  // refQuad is { date, personId, facility, sourceType }
  if (!refQuad.facility) {
    const emrClient = DA.getEmrProspectDetail(refQuad.personId);
    const facilityName = emrClient ? emrClient.facilityName : 'Unassigned';
    refQuad = { ...refQuad, facility: facilityName };
  }
  if (!refQuad.sourceType) {
    refQuad = { ...refQuad, sourceType: 'Unknown' };
  }
  return refQuad;
};

const replaceDateStringsWithDates = (refQuad) => {
  // refQuad is { date, personId, facility, sourceType } where 'date' is an ISO 8601 string
  return { ...refQuad, date: DateTime.fromISO(refQuad.date).toLocal() };
};

const extractSourceTypesInOrder = (triples) => {
  let sourceTypes = [];
  let foundUnknown = false;
  triples.forEach((trip) => {
    if (trip.sourceType === 'Unknown') {
      foundUnknown = true;
    } else {
      if (sourceTypes.indexOf(trip.sourceType) < 0) {
        sourceTypes = [...sourceTypes, trip.sourceType];
      }
    }
  });
  sourceTypes.sort();
  if (foundUnknown) {
    sourceTypes = [...sourceTypes, 'Unknown'];
  }
  return sourceTypes;
};

const extractFacilitiesInOrder = (triples) => {
  let facilities = [];
  let foundUnknown = false;
  triples.forEach((trip) => {
    if (trip.facility === 'Unassigned') {
      foundUnknown = true;
    } else {
      if (facilities.indexOf(trip.facility) < 0) {
        facilities = [...facilities, trip.facility];
      }
    }
  });
  facilities.sort();
  if (foundUnknown) {
    facilities = [...facilities, 'Unassigned'];
  }
  return facilities;
};

const ReferralReportPage = () => {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const [startDate, setStartDate] = useState(DateTime.local().startOf('day').minus({ months: 1 }).plus({ days: 1 }));
  const [lastIncludedDate, setLastIncludedDate] = useState(DateTime.local().startOf('day'));
  const [allFacilities, setAllFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [allSourceTypes, setAllSourceTypes] = useState([]);
  const [referralTriples, setReferralTriples] = useState([]);

  const setStateFromReferralStats = (refStats) => {
    refStats = refStats.map((refStat) => replaceNulls(refStat));
    refStats = refStats.map((refStat) => replaceDateStringsWithDates(refStat));
    setReferralTriples(refStats);
    setAllSourceTypes(extractSourceTypesInOrder(refStats));
    const facilitiesList = extractFacilitiesInOrder(refStats);
    setAllFacilities(facilitiesList);
    setSelectedFacilities(facilitiesList);
  };

  const orderAndSetSelectedFacilities = (someFacilities) => {
    // 'someFacilities is a subset of 'allFacilities'
    // our goal here is to generate a list of the same facilities, but in the order they are in in 'allFacilities'
    // then we'll call setSelected facilities with that list
    let ordered = [];
    allFacilities.forEach((knownFac) => {
      if (someFacilities.indexOf(knownFac) >= 0) ordered = [...ordered, knownFac];
    });
    setSelectedFacilities(ordered);
  };

  const getTriples = useCallback(async () => {
    try {
      const response = await axios.get('/api/firm/referralStats');

      if (isMountedRef.current) {
        const refStats = response.data.refStats;
        setStateFromReferralStats(refStats);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  useEffect(() => {
    getTriples();
  }, [getTriples]);

  const download = async () => {
    const firmName = DA.getLoggedInFirmInfo().firmName;
    const cutoffTime = lastIncludedDate.plus(Duration.fromObject({ days: 1 }));
    try {
      const postBody = {
        firmName,
        firstDate: startDate.toISO(),
        lastDate: cutoffTime.toISO(),
        facilities: selectedFacilities,
        // the next two fields are included in order to allow the server to simply
        // reproduce the table shown in the preview
        sourceTypes: allSourceTypes,
        // fasts: FacilityAndSourceType-s
        fasts: referralTriples.map((trip) => {
          return { facility: trip.facility, sourceType: trip.sourceType };
        }),
      };

      const response = await axios.request({
        responseType: 'blob',
        // url: '/api/firm/referralReport',
        url: '/api/firm/referralReport2', // refferalReport2 is the one that just reproduces the preview
        method: 'post',
        headers: {
          Accept: 'application/vnd.oasis.opendocument.spreadsheet',
        },
        data: postBody,
      });

      // when we get a response, open up an invisible link and click it
      const fileBuffer = response.data;
      const blob = new Blob([fileBuffer], { type: 'application/vnd.oasis.opendocument.spreadsheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'referralReport.ods';
      a.click();
    } catch (err) {
      console.error(err);
    }
  };

  if (referralTriples.length === 0) {
    return null;
  }

  return (
    <Container className={classes.root} maxWidth={false}>
      <Grid container spacing={2}>
        <Grid item md={12}>
          <ControlPanel
            startDate={startDate}
            startDateSetter={setStartDate}
            lastIncludedDate={lastIncludedDate}
            lastIncludedDateSetter={setLastIncludedDate}
            allFacilities={allFacilities}
            selectedFacilities={selectedFacilities}
            selectedFacilitiesSetter={orderAndSetSelectedFacilities}
            downloadFunc={() => download()}
          />
        </Grid>
      </Grid>
      <Grid item md={12}>
        <ReportPreview
          startDate={startDate}
          lastIncludedDate={lastIncludedDate}
          allFacilities={allFacilities}
          selectedFacilities={selectedFacilities}
          sourceTypes={allSourceTypes}
          referralTriples={referralTriples}
        />
      </Grid>
    </Container>
  );
};

export default ReferralReportPage;
